import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { jobsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import {
  RefreshCw,
  RotateCcw,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Zap,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const JobsPage = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [retryingAll, setRetryingAll] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const tenantId = user?.tenant_id || 'default';

  const loadJobs = useCallback(async () => {
    try {
      const params = { tenant_id: tenantId };
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await jobsAPI.listOCRJobs(params);
      setJobs(data.jobs || []);
      setStatusCounts(data.status_counts || {});
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, statusFilter]);

  useEffect(() => {
    loadJobs();
    const interval = setInterval(loadJobs, 8000);
    return () => clearInterval(interval);
  }, [loadJobs]);

  const handleRetry = async (jobId) => {
    setRetrying(jobId);
    try {
      await jobsAPI.retryJob(jobId);
      toast.success('Job queued for retry');
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(null);
    }
  };

  const handleRetryAll = async () => {
    setRetryingAll(true);
    try {
      const { data } = await jobsAPI.retryAllFailed(tenantId);
      toast.success(`${data.retried} job(s) queued for retry`);
      loadJobs();
    } catch (err) {
      toast.error('Failed to retry jobs');
    } finally {
      setRetryingAll(false);
    }
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'queued': return <Clock className="h-4 w-4 text-amber-500" />;
      case 'processing': return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'completed': return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-slate-400" />;
    }
  };

  const statusBadge = (status) => {
    const cfg = {
      queued: 'bg-amber-50 text-amber-700 border-amber-200',
      processing: 'bg-blue-50 text-blue-700 border-blue-200',
      completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      failed: 'bg-red-50 text-red-700 border-red-200',
    };
    return (
      <Badge variant="outline" className={cfg[status] || 'bg-slate-50 text-slate-700'}>
        <span className="flex items-center gap-1.5">
          {statusIcon(status)}
          {status}
        </span>
      </Badge>
    );
  };

  const totalJobs = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const failedCount = statusCounts.failed || 0;

  return (
    <div className="space-y-6" data-testid="jobs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Background Jobs</h1>
          <p className="text-slate-500 mt-1">Monitor OCR processing and background tasks</p>
        </div>
        <div className="flex gap-2">
          {failedCount > 0 && (
            <Button
              variant="outline"
              onClick={handleRetryAll}
              disabled={retryingAll}
              className="border-red-200 text-red-700 hover:bg-red-50"
              data-testid="retry-all-failed-btn"
            >
              {retryingAll ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              Retry All Failed ({failedCount})
            </Button>
          )}
          <Button variant="outline" onClick={loadJobs} data-testid="refresh-jobs-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-slate-400">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase">Total Jobs</p>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="total-jobs-count">{totalJobs}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-400">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-amber-600 uppercase">Queued</p>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="queued-jobs-count">{statusCounts.queued || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-400">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-blue-600 uppercase">Processing</p>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="processing-jobs-count">{statusCounts.processing || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-red-600 uppercase">Failed</p>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="failed-jobs-count">{failedCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter + Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">OCR Jobs</CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="job-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14" />)}
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-16">
              <Zap className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-slate-900">No jobs found</h3>
              <p className="text-slate-500 mt-1">Upload documents to start OCR processing</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job ID</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id} data-testid={`job-row-${job.id}`}>
                    <TableCell className="font-mono text-xs text-slate-500">
                      {job.id.slice(0, 20)}...
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {job.document_id?.slice(0, 12)}...
                    </TableCell>
                    <TableCell className="capitalize text-sm">{job.document_type || '-'}</TableCell>
                    <TableCell>{statusBadge(job.status)}</TableCell>
                    <TableCell>
                      {job.overall_confidence != null ? (
                        <span className={`font-mono text-sm ${
                          job.overall_confidence >= 0.85 ? 'text-emerald-600' :
                          job.overall_confidence >= 0.7 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {(job.overall_confidence * 100).toFixed(0)}%
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {job.created_at ? formatDistanceToNow(new Date(job.created_at), { addSuffix: true }) : '-'}
                    </TableCell>
                    <TableCell>
                      {job.status === 'failed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRetry(job.id)}
                          disabled={retrying === job.id}
                          data-testid={`retry-job-${job.id}`}
                        >
                          {retrying === job.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCcw className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {job.status === 'failed' && job.error_message && (
                        <span className="block text-xs text-red-500 mt-1 max-w-[200px] truncate" title={job.error_message}>
                          {job.error_message}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JobsPage;
